import React, { useState, useCallback, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import TranslationLanguageSelector from './TranslationLanguageSelector';
import TextArea from './TextArea';
import TranslationHistory from './TranslationHistory';
import VoiceSelector from './VoiceSelector';
import TranslationService from '../services/TranslationService';
import { getLanguagesByAppLanguage } from '../data/languages';
import { useLanguage } from '../contexts/LanguageContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiRefreshCw, FiCopy, FiVolume2, FiVolumeX, FiTrash2, FiZap } = FiIcons;

const Translator = () => {
  const { t, currentLanguage } = useLanguage();
  const [sourceLanguage, setSourceLanguage] = useState('auto');
  const [targetLanguage, setTargetLanguage] = useState('en');
  const [sourceText, setSourceText] = useState('');
  const [translatedText, setTranslatedText] = useState('');
  const [isTranslating, setIsTranslating] = useState(false);
  const [detectedLanguage, setDetectedLanguage] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [copyFeedback, setCopyFeedback] = useState('');
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [currentSpeaker, setCurrentSpeaker] = useState(null);

  // Estados para selección de voces
  const [sourceVoice, setSourceVoice] = useState(null);
  const [targetVoice, setTargetVoice] = useState(null);
  const [sourceVoiceOpen, setSourceVoiceOpen] = useState(false);
  const [targetVoiceOpen, setTargetVoiceOpen] = useState(false);
  const [voicesAutoSelected, setVoicesAutoSelected] = useState({ source: false, target: false });

  const translationTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const currentTranslationId = useRef(0);
  const currentSpeechUtterance = useRef(null);

  useEffect(() => {
    const savedHistory = localStorage.getItem('translationHistory');
    if (savedHistory) {
      try {
        const parsedHistory = JSON.parse(savedHistory);
        setHistory(Array.isArray(parsedHistory) ? parsedHistory : []);
      } catch (e) {
        console.error('Error parsing translation history:', e);
        setHistory([]);
        localStorage.removeItem('translationHistory');
      }
    }

    TranslationService.preloadCommonTranslations();

    const loadVoices = () => {
      if ('speechSynthesis' in window) {
        speechSynthesis.getVoices();
      }
    };
    loadVoices();

    if ('speechSynthesis' in window) {
      speechSynthesis.addEventListener('voiceschanged', loadVoices);
    }

    return () => {
      if ('speechSynthesis' in window) {
        speechSynthesis.removeEventListener('voiceschanged', loadVoices);
      }
    };
  }, []);

  // CRITICAL FIX: Función para guardar en historial CORREGIDA COMPLETAMENTE
  const saveToHistory = useCallback((source, target, sourceLang, targetLang, detected) => {
    if (!source || !target || source.trim() === target.trim()) {
      console.log('🚫 Skipping history save - invalid translation');
      return;
    }

    const newEntry = {
      id: Date.now() + Math.random(),
      sourceText: source.trim(),
      translatedText: target.trim(),
      sourceLanguage: sourceLang,
      targetLanguage: targetLang,
      detectedLanguage: detected,
      timestamp: new Date().toISOString()
    };

    console.log('💾 SAVING TO HISTORY:', newEntry);

    setHistory(prevHistory => {
      const updatedHistory = [newEntry, ...prevHistory.slice(0, 19)];
      
      try {
        localStorage.setItem('translationHistory', JSON.stringify(updatedHistory));
        console.log('✅ History saved successfully');
      } catch (e) {
        console.error('❌ Error saving history:', e);
      }
      
      return updatedHistory;
    });
  }, []);

  // CRITICAL FIX: Función de traducción COMPLETAMENTE NUEVA Y FUNCIONAL
  const translateText = useCallback(async (text, fromLang, toLang, saveHistory = true) => {
    if (!text || !text.trim()) {
      console.log('🚫 Empty text, clearing results');
      setTranslatedText('');
      setDetectedLanguage(null);
      setIsTranslating(false);
      setError(null);
      return;
    }

    const trimmedText = text.trim();
    
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    const translationId = ++currentTranslationId.current;

    console.log(`🚀 TRANSLATION REQUEST (ID: ${translationId}):`);
    console.log(`  - Text: "${trimmedText.substring(0, 50)}..."`);
    console.log(`  - From: ${fromLang} -> To: ${toLang}`);
    console.log(`  - Save to history: ${saveHistory}`);

    setIsTranslating(true);
    setError(null);

    try {
      console.log('🔄 Calling TranslationService...');
      const result = await TranslationService.translate(trimmedText, fromLang, toLang);

      if (translationId === currentTranslationId.current && !abortControllerRef.current?.signal.aborted) {
        console.log(`✅ TRANSLATION COMPLETE (ID: ${translationId}):`);
        console.log(`  - Result: "${result.translatedText.substring(0, 50)}..."`);
        console.log(`  - Detected: ${result.detectedLanguage}`);

        setTranslatedText(result.translatedText);
        setDetectedLanguage(result.detectedLanguage);

        // CRITICAL FIX: Guardar en historial SOLO cuando se solicite
        if (saveHistory && result.translatedText && result.translatedText.trim() && 
            result.translatedText.trim() !== trimmedText.trim()) {
          console.log('💾 Saving manual translation to history');
          saveToHistory(trimmedText, result.translatedText, fromLang, toLang, result.detectedLanguage);
        }

        setError(null);
      } else {
        console.log(`⏭️ Translation ${translationId} was superseded`);
      }
    } catch (err) {
      if (translationId === currentTranslationId.current && !abortControllerRef.current?.signal.aborted) {
        console.error('❌ Translation failed:', err);
        setError('Error en la traducción. Por favor intenta nuevamente.');
        setTranslatedText('');
      }
    } finally {
      if (translationId === currentTranslationId.current) {
        setIsTranslating(false);
      }
    }
  }, [saveToHistory]);

  // CRITICAL FIX: Efecto de traducción automática COMPLETAMENTE CORREGIDO
  useEffect(() => {
    if (translationTimeoutRef.current) {
      clearTimeout(translationTimeoutRef.current);
    }

    if (!sourceText.trim()) {
      setTranslatedText('');
      setDetectedLanguage(null);
      setError(null);
      setIsTranslating(false);
      return;
    }

    console.log('⏱️ Setting up auto-translation timer...');

    const actualSourceLang = sourceLanguage === 'auto' ? (detectedLanguage || 'auto') : sourceLanguage;
    
    if (actualSourceLang === targetLanguage && sourceLanguage !== 'auto' && actualSourceLang !== 'auto') {
      console.log('⏭️ Same language detected, showing original text');
      setTranslatedText(sourceText);
      setDetectedLanguage(actualSourceLang);
      setIsTranslating(false);
      setError(null);
      return;
    }

    translationTimeoutRef.current = setTimeout(() => {
      console.log('🔄 Executing auto-translation (NO save to history)');
      translateText(sourceText, sourceLanguage, targetLanguage, false);
    }, 800);

    return () => {
      if (translationTimeoutRef.current) {
        clearTimeout(translationTimeoutRef.current);
      }
    };
  }, [sourceText, sourceLanguage, targetLanguage, translateText, detectedLanguage]);

  // CRITICAL FIX: Función "Traducir ahora" COMPLETAMENTE FUNCIONAL
  const handleTranslateNow = () => {
    if (!sourceText.trim()) {
      console.warn('🚫 No text to translate');
      return;
    }

    console.log('🎯 MANUAL TRANSLATION TRIGGERED');
    console.log(`  - Text: "${sourceText.substring(0, 50)}..."`);
    console.log(`  - From: ${sourceLanguage} -> To: ${targetLanguage}`);
    
    if (translationTimeoutRef.current) {
      clearTimeout(translationTimeoutRef.current);
    }

    // CRITICAL: Ejecutar traducción CON guardado en historial
    translateText(sourceText, sourceLanguage, targetLanguage, true);
  };

  const handleSwapLanguages = () => {
    if (sourceLanguage !== 'auto') {
      setSourceLanguage(targetLanguage);
      setTargetLanguage(sourceLanguage);
      setSourceText(translatedText);
      setTranslatedText(sourceText);
      
      const tempVoice = sourceVoice;
      setSourceVoice(targetVoice);
      setTargetVoice(tempVoice);
      
      setVoicesAutoSelected({ source: false, target: false });
    }
  };

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyFeedback('¡Copiado!');
      setTimeout(() => setCopyFeedback(''), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  const handleSpeak = async (text, languageCode, isSourceText = false) => {
    if (!('speechSynthesis' in window) || !text.trim()) {
      console.warn('🚫 Speech synthesis not available or no text');
      return;
    }

    const voiceToUse = isSourceText ? sourceVoice : targetVoice;
    const speakerType = isSourceText ? 'source' : 'target';

    if (!voiceToUse || !voiceToUse.originalVoice) {
      console.warn(`🚫 No voice selected for ${speakerType}`);
      alert(`Por favor selecciona una voz para ${isSourceText ? 'el texto original' : 'la traducción'} primero.`);
      return;
    }

    if (isSpeaking && currentSpeaker === speakerType) {
      handleStopSpeakingSafely();
      return;
    }

    if (isSpeaking) {
      handleStopSpeakingSafely();
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    let actualLanguage = languageCode;
    if (languageCode === 'auto' && detectedLanguage) {
      actualLanguage = detectedLanguage;
    } else if (languageCode === 'auto') {
      actualLanguage = TranslationService.detectLanguageFromText(text) || 'en';
    }

    console.log('🎤 Starting speech synthesis');

    try {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.voice = voiceToUse.originalVoice;
      utterance.lang = voiceToUse.lang;
      utterance.rate = 0.9;
      utterance.pitch = 1.0;
      utterance.volume = 1.0;

      utterance.onstart = () => {
        console.log('✅ Speech started');
        setIsSpeaking(true);
        setCurrentSpeaker(speakerType);
      };

      utterance.onend = () => {
        console.log('✅ Speech ended');
        setIsSpeaking(false);
        setCurrentSpeaker(null);
        currentSpeechUtterance.current = null;
      };

      utterance.onerror = (event) => {
        console.log(`🎤 Speech event: ${event.error}`);
        if (event.error === 'interrupted' || event.error === 'canceled') {
          console.log('🎤 Speech interrupted intentionally');
        } else {
          console.error(`❌ Speech error: ${event.error}`);
          alert(`Error en la síntesis de voz: ${event.error}`);
        }
        setIsSpeaking(false);
        setCurrentSpeaker(null);
        currentSpeechUtterance.current = null;
      };

      currentSpeechUtterance.current = utterance;
      speechSynthesis.cancel();
      
      setTimeout(() => {
        speechSynthesis.speak(utterance);
      }, 50);

    } catch (error) {
      console.error('❌ Error starting speech:', error);
      setIsSpeaking(false);
      setCurrentSpeaker(null);
      currentSpeechUtterance.current = null;
      alert(`Error al iniciar la síntesis de voz: ${error.message}`);
    }
  };

  const handleStopSpeakingSafely = () => {
    console.log('🛑 Stopping speech safely');
    setIsSpeaking(false);
    setCurrentSpeaker(null);
    currentSpeechUtterance.current = null;

    if ('speechSynthesis' in window) {
      speechSynthesis.cancel();
    }
  };

  const handleClearSource = () => {
    setSourceText('');
    setTranslatedText('');
    setDetectedLanguage(null);
    setError(null);
    setIsTranslating(false);

    if (translationTimeoutRef.current) {
      clearTimeout(translationTimeoutRef.current);
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  const handleClearTranslation = () => {
    setTranslatedText('');
  };

  const handleClearAll = () => {
    setSourceText('');
    setTranslatedText('');
    setDetectedLanguage(null);
    setError(null);
    setIsTranslating(false);

    if (translationTimeoutRef.current) {
      clearTimeout(translationTimeoutRef.current);
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    handleStopSpeakingSafely();
  };

  const handleHistorySelect = (entry) => {
    if (translationTimeoutRef.current) {
      clearTimeout(translationTimeoutRef.current);
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    handleStopSpeakingSafely();

    setSourceText(entry.sourceText);
    setTranslatedText(entry.translatedText);
    setSourceLanguage(entry.sourceLanguage);
    setTargetLanguage(entry.targetLanguage);
    setDetectedLanguage(entry.detectedLanguage);
    setIsTranslating(false);

    setVoicesAutoSelected({ source: false, target: false });
  };

  const clearHistory = () => {
    setHistory([]);
    localStorage.removeItem('translationHistory');
  };

  const getLanguageName = (code) => {
    const languageNames = getLanguagesByAppLanguage(currentLanguage);
    return languageNames[code] || code;
  };

  const handleSourceVoiceToggle = () => {
    setTargetVoiceOpen(false);
    setSourceVoiceOpen(!sourceVoiceOpen);
  };

  const handleTargetVoiceToggle = () => {
    setSourceVoiceOpen(false);
    setTargetVoiceOpen(!targetVoiceOpen);
  };

  const getSourceVoiceLanguage = () => {
    if (sourceLanguage === 'auto' && detectedLanguage) {
      return detectedLanguage;
    } else if (sourceLanguage === 'auto') {
      return 'en';
    }
    return sourceLanguage;
  };

  const handleSourceVoiceChange = (voice) => {
    console.log(`🎤 Source voice selected: ${voice.name}`);
    setSourceVoice(voice);
    setVoicesAutoSelected(prev => ({ ...prev, source: true }));
  };

  const handleTargetVoiceChange = (voice) => {
    console.log(`🎤 Target voice selected: ${voice.name}`);
    setTargetVoice(voice);
    setVoicesAutoSelected(prev => ({ ...prev, target: true }));
  };

  const handleSourceVoiceAutoSelect = (voice) => {
    if (!voicesAutoSelected.source && voice) {
      console.log(`🎯 Auto-selecting source voice: ${voice.name}`);
      setSourceVoice(voice);
      setVoicesAutoSelected(prev => ({ ...prev, source: true }));
    }
  };

  const handleTargetVoiceAutoSelect = (voice) => {
    if (!voicesAutoSelected.target && voice) {
      console.log(`🎯 Auto-selecting target voice: ${voice.name}`);
      setTargetVoice(voice);
      setVoicesAutoSelected(prev => ({ ...prev, target: true }));
    }
  };

  const canSpeak = (isSourceText) => {
    const voice = isSourceText ? sourceVoice : targetVoice;
    const text = isSourceText ? sourceText : translatedText;
    return voice && voice.originalVoice && text.trim() && 'speechSynthesis' in window;
  };

  const getSpeakButtonState = (isSourceText) => {
    const speakerType = isSourceText ? 'source' : 'target';
    const isCurrentlySpeaking = isSpeaking && currentSpeaker === speakerType;
    
    return {
      isActive: isCurrentlySpeaking,
      canSpeak: canSpeak(isSourceText),
      icon: isCurrentlySpeaking ? FiVolumeX : FiVolume2,
      title: isCurrentlySpeaking ? 'Detener' : 'Escuchar'
    };
  };

  useEffect(() => {
    const actualSourceLanguage = getSourceVoiceLanguage();
    if (actualSourceLanguage && actualSourceLanguage !== 'auto') {
      console.log(`🎤 Language changed, resetting voice selection for: ${actualSourceLanguage}`);
      setVoicesAutoSelected(prev => ({ ...prev, source: false }));
    }
  }, [detectedLanguage, sourceLanguage]);

  useEffect(() => {
    if (targetLanguage) {
      console.log(`🎤 Target language changed, resetting voice selection for: ${targetLanguage}`);
      setVoicesAutoSelected(prev => ({ ...prev, target: false }));
    }
  }, [targetLanguage]);

  useEffect(() => {
    return () => {
      if (translationTimeoutRef.current) {
        clearTimeout(translationTimeoutRef.current);
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      if ('speechSynthesis' in window) {
        speechSynthesis.cancel();
      }
    };
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="max-w-6xl mx-auto translator-container"
    >
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="text-center mb-8"
      >
        <h2 className="text-3xl md:text-4xl font-bold text-gray-800 dark:text-white mb-2">
          {t('translateAnything')}
        </h2>
        <p className="text-gray-600 dark:text-gray-300 text-lg">
          {t('poweredBy')}
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-xl border border-gray-200/20 dark:border-gray-700/20 p-6 md:p-8 mb-8 relative overflow-visible"
      >
        {/* Language Selectors */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="w-full">
            <TranslationLanguageSelector
              value={sourceLanguage}
              onChange={setSourceLanguage}
              includeAuto={true}
              label={t('from')}
            />
            {detectedLanguage && sourceLanguage === 'auto' && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-sm text-indigo-600 dark:text-indigo-400 mt-2"
              >
                {t('detected')}: {getLanguageName(detectedLanguage)}
              </motion.p>
            )}
          </div>

          <div className="flex justify-center items-center">
            <div className="flex items-center justify-center h-[3.5rem]">
              <motion.button
                whileHover={{ scale: 1.1, rotate: 180 }}
                whileTap={{ scale: 0.9 }}
                onClick={handleSwapLanguages}
                disabled={sourceLanguage === 'auto'}
                className="p-3 rounded-full bg-indigo-500 hover:bg-indigo-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white shadow-lg transition-all duration-200"
              >
                <SafeIcon icon={FiRefreshCw} className="w-5 h-5" />
              </motion.button>
            </div>
          </div>

          <div className="w-full">
            <TranslationLanguageSelector
              value={targetLanguage}
              onChange={setTargetLanguage}
              includeAuto={false}
              label={t('to')}
            />
          </div>
        </div>

        {/* Translation Areas */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Source Text */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="space-y-3"
          >
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-700 dark:text-gray-300">
                {t('enterText')}
              </h3>
              <div className="flex items-center space-x-2">
                {sourceText && (
                  <>
                    <VoiceSelector
                      languageCode={getSourceVoiceLanguage()}
                      selectedVoice={sourceVoice}
                      onVoiceChange={handleSourceVoiceChange}
                      onAutoVoiceSelect={handleSourceVoiceAutoSelect}
                      shouldAutoSelect={!voicesAutoSelected.source}
                      isOpen={sourceVoiceOpen}
                      onToggle={handleSourceVoiceToggle}
                      onClose={() => setSourceVoiceOpen(false)}
                      label={t('sourceVoice') || 'Voz fuente'}
                    />
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => handleSpeak(sourceText, getSourceVoiceLanguage(), true)}
                      disabled={!canSpeak(true)}
                      className={`p-2 transition-colors ${
                        canSpeak(true)
                          ? `${getSpeakButtonState(true).isActive
                              ? 'text-orange-500 bg-orange-100 dark:bg-orange-900/30'
                              : 'text-gray-500 hover:text-indigo-500'}`
                          : 'text-gray-300 cursor-not-allowed'
                      }`}
                      title={canSpeak(true) ? getSpeakButtonState(true).title : 'Selecciona una voz primero'}
                    >
                      <SafeIcon
                        icon={getSpeakButtonState(true).icon}
                        className={`w-4 h-4 ${getSpeakButtonState(true).isActive ? 'animate-pulse' : ''}`}
                      />
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => handleCopy(sourceText)}
                      className="p-2 text-gray-500 hover:text-indigo-500 transition-colors"
                      title={t('copy')}
                    >
                      <SafeIcon icon={FiCopy} className="w-4 h-4" />
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={handleClearSource}
                      className="p-2 text-gray-500 hover:text-red-500 transition-colors"
                      title={t('clear')}
                    >
                      <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                    </motion.button>
                  </>
                )}
              </div>
            </div>
            <TextArea
              value={sourceText}
              onChange={setSourceText}
              placeholder={t('typeHere')}
              className="h-40"
            />
          </motion.div>

          {/* Translated Text */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="space-y-3"
          >
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-700 dark:text-gray-300">
                {t('translation')}
              </h3>
              <div className="flex items-center space-x-2">
                {translatedText && !isTranslating && (
                  <>
                    <VoiceSelector
                      languageCode={targetLanguage}
                      selectedVoice={targetVoice}
                      onVoiceChange={handleTargetVoiceChange}
                      onAutoVoiceSelect={handleTargetVoiceAutoSelect}
                      shouldAutoSelect={!voicesAutoSelected.target}
                      isOpen={targetVoiceOpen}
                      onToggle={handleTargetVoiceToggle}
                      onClose={() => setTargetVoiceOpen(false)}
                      label={t('targetVoice') || 'Voz traducción'}
                    />
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => handleSpeak(translatedText, targetLanguage, false)}
                      disabled={!canSpeak(false)}
                      className={`p-2 transition-colors ${
                        canSpeak(false)
                          ? `${getSpeakButtonState(false).isActive
                              ? 'text-orange-500 bg-orange-100 dark:bg-orange-900/30'
                              : 'text-gray-500 hover:text-indigo-500'}`
                          : 'text-gray-300 cursor-not-allowed'
                      }`}
                      title={canSpeak(false) ? getSpeakButtonState(false).title : 'Selecciona una voz primero'}
                    >
                      <SafeIcon
                        icon={getSpeakButtonState(false).icon}
                        className={`w-4 h-4 ${getSpeakButtonState(false).isActive ? 'animate-pulse' : ''}`}
                      />
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => handleCopy(translatedText)}
                      className="p-2 text-gray-500 hover:text-indigo-500 transition-colors"
                      title={t('copy')}
                    >
                      <SafeIcon icon={FiCopy} className="w-4 h-4" />
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={handleClearTranslation}
                      className="p-2 text-gray-500 hover:text-red-500 transition-colors"
                      title={t('clear')}
                    >
                      <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                    </motion.button>
                  </>
                )}
              </div>
            </div>
            <div className="relative">
              <TextArea
                value={translatedText}
                onChange={() => {}}
                placeholder={t('translationHere')}
                className="h-40"
                readOnly
              />
              {isTranslating && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="absolute inset-0 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-lg flex items-center justify-center"
                >
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full"
                  />
                </motion.div>
              )}
            </div>
          </motion.div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap items-center justify-between mt-6 gap-4">
          <div className="flex items-center space-x-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleTranslateNow}
              disabled={!sourceText.trim() || isTranslating}
              className="flex items-center space-x-2 px-4 py-2 bg-indigo-500 hover:bg-indigo-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-all duration-200"
            >
              <SafeIcon icon={FiZap} className="w-4 h-4" />
              <span>{t('translateNow')}</span>
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleClearAll}
              className="flex items-center space-x-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-all duration-200"
            >
              <SafeIcon icon={FiTrash2} className="w-4 h-4" />
              <span>{t('clearAll')}</span>
            </motion.button>
            {isSpeaking && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleStopSpeakingSafely}
                className="flex items-center space-x-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-all duration-200"
              >
                <SafeIcon icon={FiVolumeX} className="w-4 h-4" />
                <span>Detener audio</span>
              </motion.button>
            )}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {sourceText.length} / 20000 {t('characters')}
          </div>
        </div>

        {error && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-lg text-red-700 dark:text-red-300 text-sm"
          >
            {error}
          </motion.div>
        )}

        {copyFeedback && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="fixed top-20 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-50"
          >
            {copyFeedback}
          </motion.div>
        )}
      </motion.div>

      {/* Translation History */}
      <div className="history-container">
        <TranslationHistory
          history={history}
          onSelectTranslation={handleHistorySelect}
          onClearHistory={clearHistory}
        />
      </div>
    </motion.div>
  );
};

export default Translator;